
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CreditCard, Plus, Trash2, Edit, CheckCircle } from 'lucide-react';
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

// Mock payment methods
const mockPaymentMethods = [
  { id: 1, type: 'card', last4: '4242', brand: 'Visa', expMonth: 12, expYear: 2024, isDefault: true },
  { id: 2, type: 'card', last4: '1234', brand: 'Mastercard', expMonth: 8, expYear: 2025, isDefault: false },
];

const PaymentMethods = () => {
  const { toast } = useToast();
  const [paymentMethods, setPaymentMethods] = useState(mockPaymentMethods);
  const [isAddCardDialogOpen, setIsAddCardDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);

  const handleAddCard = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would call the payment processor API
    const newCard = {
      id: paymentMethods.length + 1,
      type: 'card',
      last4: '5678',
      brand: 'Visa',
      expMonth: 9,
      expYear: 2026,
      isDefault: false,
    };
    
    setPaymentMethods([...paymentMethods, newCard]);
    toast({
      title: "Card added successfully",
      description: "Your new payment method has been saved.",
    });
    setIsAddCardDialogOpen(false);
  };

  const handleDelete = () => {
    if (selectedMethodId) {
      const updatedMethods = paymentMethods.filter(method => method.id !== selectedMethodId);
      
      // If we're deleting the default method, make the first remaining one the default
      if (paymentMethods.find(m => m.id === selectedMethodId)?.isDefault && updatedMethods.length > 0) {
        updatedMethods[0].isDefault = true;
      }
      
      setPaymentMethods(updatedMethods);
      toast({
        title: "Payment method removed",
        description: "The payment method has been deleted from your account.",
      });
      setIsDeleteDialogOpen(false);
      setSelectedMethodId(null);
    }
  };

  const handleSetDefault = (id: number) => {
    setPaymentMethods(
      paymentMethods.map(method => ({
        ...method,
        isDefault: method.id === id,
      }))
    );
    
    toast({
      title: "Default payment method updated",
      description: "Your default payment method has been changed.",
    });
  };

  const openDeleteDialog = (id: number) => {
    setSelectedMethodId(id);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Payment Methods</h2>
        <Button 
          onClick={() => setIsAddCardDialogOpen(true)} 
          className="bg-directpress-blue hover:bg-blue-800"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Payment Method
        </Button>
      </div>

      <div className="space-y-4">
        {paymentMethods.map((method) => (
          <Card key={method.id} className={method.isDefault ? 'border-directpress-blue' : ''}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <div className="bg-muted p-3 rounded-full mr-4">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-medium flex items-center">
                      {method.brand} •••• {method.last4}
                      {method.isDefault && (
                        <span className="ml-2 text-xs bg-directpress-blue/10 text-directpress-blue px-2 py-1 rounded-full">
                          Default
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Expires {method.expMonth}/{method.expYear}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {!method.isDefault && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleSetDefault(method.id)}
                    >
                      Set as Default
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => openDeleteDialog(method.id)}
                    className="h-8 w-8"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {paymentMethods.length === 0 && (
          <Card>
            <CardContent className="p-6 flex flex-col items-center justify-center text-center py-12">
              <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No payment methods</h3>
              <p className="text-muted-foreground mb-4">
                Add a payment method to subscribe or make purchases.
              </p>
              <Button 
                onClick={() => setIsAddCardDialogOpen(true)} 
                className="bg-directpress-blue hover:bg-blue-800"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Payment Method
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Information */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Payment Security</CardTitle>
          <CardDescription>
            Your payment information is securely processed and stored.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-medium">PCI Compliant</h4>
                <p className="text-sm text-muted-foreground">
                  We adhere to the highest standards of payment card industry security.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-medium">Encrypted Transactions</h4>
                <p className="text-sm text-muted-foreground">
                  All payment information is encrypted with bank-level security.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-medium">No Stored Card Details</h4>
                <p className="text-sm text-muted-foreground">
                  Your full card details are never stored on our servers.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Card Dialog */}
      <Dialog open={isAddCardDialogOpen} onOpenChange={setIsAddCardDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Enter your card details to add a new payment method.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCard}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="card-name" className="text-right">
                  Name on Card
                </Label>
                <input
                  id="card-name"
                  className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="card-number" className="text-right">
                  Card Number
                </Label>
                <input
                  id="card-number"
                  className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="4242 4242 4242 4242"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="expiry" className="text-right">
                  Expiry Date
                </Label>
                <div className="col-span-1">
                  <input
                    id="expiry"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="MM/YY"
                    required
                  />
                </div>
                <Label htmlFor="cvc" className="text-right">
                  CVC
                </Label>
                <div className="col-span-1">
                  <input
                    id="cvc"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="123"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <div className="col-span-4">
                  <Label htmlFor="default-method" className="flex items-center space-x-2">
                    <input
                      id="default-method"
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-directpress-blue focus:ring-directpress-blue"
                    />
                    <span>Set as default payment method</span>
                  </Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit">Save Card</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Remove Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this payment method? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethods;
